import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { formatPlainDate } from "@/common/utils/format-plain-date";
import { useTRPC } from "@/trpc";

export type EndedRelationshipLookup = {
  organizationBusinessUnitId: string;
  providerUserId: string;
};

/**
 * Org-side notice for a chat whose provider is no longer on the roster.
 * Messaging stays open, but profile and credential access does not.
 */
export function EndedRelationshipNotice({
  organizationBusinessUnitId,
  providerUserId,
}: EndedRelationshipLookup) {
  const trpc = useTRPC();
  const { data } = useQuery(
    trpc.organizationBusinessUnitAffiliation.getEndedRelationship.queryOptions({
      organizationBusinessUnitId,
      providerUserId,
    }),
  );

  if (!data) {
    return null;
  }

  return (
    <div
      role="status"
      className="mx-3 mb-2 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
      <p>
        Your relationship with this provider ended on{" "}
        {formatPlainDate(data.endDate)}. You can still message them, but you no
        longer have access to their profile or credentials.
      </p>
    </div>
  );
}
